/**
 * Monitoring and Error Tracking Service
 * Centralized logging, error tracking, and performance monitoring
 */

interface ErrorLog {
  message: string;
  stack?: string;
  component?: string;
  userId?: string;
  timestamp: string;
  severity: 'error' | 'warning' | 'info';
  metadata?: Record<string, any>;
}

interface PerformanceMetric {
  name: string;
  duration: number;
  timestamp: string;
  metadata?: Record<string, any>;
}

class MonitoringService {
  private isDevelopment = import.meta.env.DEV;
  
  /**
   * Log errors to console and could be extended to external services
   */
  logError(error: Error | string, context?: {
    component?: string;
    userId?: string;
    severity?: 'error' | 'warning' | 'info';
    metadata?: Record<string, any>;
  }) {
    const errorLog: ErrorLog = {
      message: typeof error === 'string' ? error : error.message,
      stack: typeof error === 'string' ? undefined : error.stack,
      component: context?.component,
      userId: context?.userId,
      timestamp: new Date().toISOString(),
      severity: context?.severity || 'error',
      metadata: context?.metadata,
    };

    // Log to console in development
    if (this.isDevelopment) {
      console.error('[Monitoring]', errorLog);
    }

    // Store in localStorage for debugging
    this.storeErrorLog(errorLog);

    // Here you could send to external service like Sentry, LogRocket, etc.
    // Example: this.sendToExternalService(errorLog);
  }

  /**
   * Track performance metrics
   */
  trackPerformance(name: string, duration: number, metadata?: Record<string, any>) {
    const metric: PerformanceMetric = {
      name,
      duration,
      timestamp: new Date().toISOString(),
      metadata,
    };

    if (this.isDevelopment) {
      console.log('[Performance]', metric);
    }

    // Store performance metrics
    this.storePerformanceMetric(metric);
  }

  /**
   * Track user actions for analytics
   */
  trackEvent(eventName: string, properties?: Record<string, any>) {
    const event = {
      name: eventName,
      properties,
      timestamp: new Date().toISOString(),
    };

    if (this.isDevelopment) {
      console.log('[Analytics]', event);
    }

    // Store for analytics
    this.storeAnalyticsEvent(event);
  }

  /**
   * Measure function execution time
   */
  async measureAsync<T>(
    name: string,
    fn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const startTime = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - startTime;
      this.trackPerformance(name, duration, metadata);
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.trackPerformance(name, duration, { ...metadata, error: true });
      throw error;
    }
  }

  /**
   * Store error logs in localStorage (last 50)
   */
  private storeErrorLog(log: ErrorLog) {
    try {
      const logs = this.getStoredLogs();
      logs.unshift(log);
      // Keep only last 50 errors
      const recentLogs = logs.slice(0, 50);
      localStorage.setItem('corynt_error_logs', JSON.stringify(recentLogs));
    } catch (e) {
      // Failed to store, ignore
    }
  }

  /**
   * Store performance metrics in localStorage (last 100)
   */
  private storePerformanceMetric(metric: PerformanceMetric) {
    try {
      const metrics = this.getStoredMetrics();
      metrics.unshift(metric);
      const recentMetrics = metrics.slice(0, 100);
      localStorage.setItem('corynt_performance', JSON.stringify(recentMetrics));
    } catch (e) {
      // Failed to store, ignore
    }
  }

  /**
   * Store analytics events in localStorage (last 100)
   */
  private storeAnalyticsEvent(event: any) {
    try {
      const events = this.getStoredEvents();
      events.unshift(event);
      const recentEvents = events.slice(0, 100);
      localStorage.setItem('corynt_analytics', JSON.stringify(recentEvents));
    } catch (e) {
      // Failed to store, ignore
    }
  }

  /**
   * Get stored error logs
   */
  getStoredLogs(): ErrorLog[] {
    try {
      const logs = localStorage.getItem('corynt_error_logs');
      return logs ? JSON.parse(logs) : [];
    } catch {
      return [];
    }
  }

  /**
   * Get stored performance metrics
   */
  getStoredMetrics(): PerformanceMetric[] {
    try {
      const metrics = localStorage.getItem('corynt_performance');
      return metrics ? JSON.parse(metrics) : [];
    } catch {
      return [];
    }
  }

  /**
   * Get stored analytics events
   */
  getStoredEvents(): any[] {
    try {
      const events = localStorage.getItem('corynt_analytics');
      return events ? JSON.parse(events) : [];
    } catch {
      return [];
    }
  }

  /**
   * Clear all stored monitoring data
   */
  clearStoredData() {
    localStorage.removeItem('corynt_error_logs');
    localStorage.removeItem('corynt_performance');
    localStorage.removeItem('corynt_analytics');
  }

  /**
   * Get monitoring dashboard data
   */
  getDashboardData() {
    return {
      errors: this.getStoredLogs(),
      performance: this.getStoredMetrics(),
      events: this.getStoredEvents(),
    };
  }
}

// Export singleton instance
export const monitoring = new MonitoringService();

// Helper function to wrap async functions with error tracking
export function withErrorTracking<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: { component: string }
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      monitoring.logError(error as Error, {
        component: context.component,
        severity: 'error',
      });
      throw error;
    }
  }) as T;
}

// Helper to track component mount/unmount
export function useComponentTracking(componentName: string) {
  const mountTime = performance.now();
  
  return () => {
    const unmountTime = performance.now();
    monitoring.trackPerformance(
      `${componentName}_lifecycle`,
      unmountTime - mountTime,
      { event: 'mount_to_unmount' }
    );
  };
}
